import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MessageCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { telehealthServices } from '../data/telehealthServices';
import { SITE_NAME } from '../siteConfig';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const formRef = useRef();

  const EMAILJS_SERVICE_ID = 'service_jg4bsqv';
  const EMAILJS_TEMPLATE_ID = 'template_ezxw0c7';
  const EMAILJS_PUBLIC_KEY = 'e7DBZNZoVHxRrvMDE';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone,
          service_interest: formData.service,
          message: formData.message,
        },
        EMAILJS_PUBLIC_KEY
      );

      if (result.status === 200) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: '',
        });
        formRef.current.reset();
      }
    } catch (error) {
      console.error('EmailJS Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <section
        className="contact-hero"
        style={{
          backgroundImage: 'url(/images/contact-us.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            zIndex: 1,
          }}
        />
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            className="contact-hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="contact-title">Contact us</h1>
            <p className="contact-subtitle">
              Call, email, or text — we are here to help you schedule telehealth care or answer questions about services
              and fees.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="container">
        <section className="contact-info-section">
          <div className="contact-grid">
            <motion.div
              className="contact-form"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="section-title">Send a message</h2>
              <form ref={formRef} className="contact-form-element" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Full name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone (optional)</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="service">Service interest</label>
                  <select id="service" name="service" value={formData.service} onChange={handleInputChange}>
                    <option value="">Select a service</option>
                    {telehealthServices.map((s) => (
                      <option key={s.slug} value={s.slug}>
                        {s.title} ({s.fee} {s.feeDetail})
                      </option>
                    ))}
                    <option value="general">General question</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                {submitStatus === 'success' && (
                  <div className="submit-success">
                    <p>Thank you! Your message has been sent.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="submit-error">
                    <p>Something went wrong. Please try again or reach out by phone or email.</p>
                  </div>
                )}

                <button type="submit" className={`submit-btn ${isSubmitting ? 'submitting' : ''}`} disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send message'}
                </button>
              </form>
            </motion.div>

            <motion.div
              className="contact-details"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="section-title">Get in touch</h2>
              <div className="company-name">
                <h3>{SITE_NAME}</h3>
              </div>
              <p style={{ color: '#4a5568', lineHeight: 1.6, marginBottom: '1.25rem' }}>
                Telehealth is not for emergencies. For urgent or life-threatening conditions, seek emergency care
                immediately.
              </p>
              <div className="contact-methods">
                <a className="contact-method" href="tel:+13212093211">
                  <Phone className="contact-icon" />
                  <div>
                    <h3>Call</h3>
                    <p>Use the phone number provided by the practice for scheduling.</p>
                  </div>
                </a>
                <a className="contact-method" href="mailto:clinician@openarmshcs.com">
                  <Mail className="contact-icon" />
                  <div>
                    <h3>Email</h3>
                    <p>Send a message through this page or use the practice email address you were given.</p>
                  </div>
                </a>
                <a className="contact-method" href="sms:+13212093211">
                  <MessageCircle className="contact-icon" />
                  <div>
                    <h3>Text</h3>
                    <p>Text when SMS coordination is completed—we will confirm availability.</p>
                  </div>
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Contact;
